import type { Guest, DragState, UIState, GuestFilterMode, GuestSortField, CustomFieldConfig, NamedVersion, HistoryState, HistoryAction, HistoryActionType, VersionState } from './types.ts';
import { HISTORY_CONFIG } from './constants.ts';

// Generic state store with TypeScript support
export class StateStore<T> {
  private value: T;
  private listeners = new Set<(newValue: T, oldValue: T | undefined) => void>();

  constructor(initialValue: T) {
    this.value = initialValue;
  }

  get(): T {
    return this.value;
  }

  set(newValue: T): void {
    const oldValue = this.value;
    this.value = newValue;
    this.listeners.forEach(listener => listener(newValue, oldValue));
  }

  subscribe(listener: (newValue: T, oldValue: T | undefined) => void): () => void {
    this.listeners.add(listener);
    // Call immediately with current value
    listener(this.value, undefined);
    
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  listen(listener: (newValue: T, oldValue: T | undefined) => void): () => void {
    this.listeners.add(listener);
    // Don't call immediately, only on future changes
    return () => this.listeners.delete(listener);
  }
}

export class ComputedStore<T, S extends readonly StateStore<any>[]> {
  private value: T;
  private listeners = new Set<(newValue: T, oldValue: T | undefined) => void>();

  constructor(
    private sourceStores: S,
    private computeFn: (...values: { [K in keyof S]: S[K] extends StateStore<infer U> ? U : never }) => T
  ) {
    this.value = this.compute();
    
    // Subscribe to all source stores
    this.sourceStores.forEach(store => {
      store.subscribe(() => {
        const oldValue = this.value;
        this.value = this.compute();
        this.listeners.forEach(listener => listener(this.value, oldValue));
      });
    });
  }

  private compute(): T {
    const values = this.sourceStores.map(store => store.get()) as { [K in keyof S]: S[K] extends StateStore<infer U> ? U : never };
    return this.computeFn(...values);
  }

  get(): T {
    return this.value;
  }

  subscribe(listener: (newValue: T, oldValue: T | undefined) => void): () => void {
    this.listeners.add(listener);
    listener(this.value, undefined);
    return () => this.listeners.delete(listener);
  }

  listen(listener: (newValue: T, oldValue: T | undefined) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

// Factory functions
export const atom = <T>(initialValue: T): StateStore<T> => new StateStore(initialValue);

// Initial data with some sample guests
const initialGuests: Guest[] = [
];

// Main business state
export const $guests = atom<Guest[]>(initialGuests);

// Drag and drop state
export const $dragState = atom<DragState>({
  isDragging: false,
  draggedGuestId: null,
  hoveredSeat: null,
  originalSeat: null,
  originalPosition: null
});

// UI state
export const $uiState = atom<UIState>({
  showDropZones: false,
  stagePosition: { x: 0, y: 0 },
  stageScale: 1
});

// Computed values
export const $occupiedSeats = new ComputedStore([$guests] as const, (guests) => {
  const occupied = new Set<string>();
  guests.forEach(guest => {
    if (guest.table && guest.seat) {
      occupied.add(`${guest.table}-${guest.seat}`);
    }
  });
  return occupied;
});

export const $unplacedGuests = new ComputedStore([$guests] as const, (guests) => {
  return guests.filter(guest => !guest.table || !guest.seat);
});

export const $placedGuests = new ComputedStore([$guests] as const, (guests) => {
  return guests.filter(guest => guest.table && guest.seat);
});

// Guest list UI state
export const $guestFilter = atom<string>('');
export const $guestFilterMode = atom<GuestFilterMode>('all');
export const $guestSortField = atom<GuestSortField>('fullName');
export const $guestSortDirection = atom<'asc' | 'desc'>('asc');

// Custom field configuration
export const $customFields = atom<CustomFieldConfig[]>([]);

// Version Management State
export const $currentVersionId = atom<string | null>(null);
export const $availableVersions = atom<NamedVersion[]>([]);
export const $isAutoSaving = atom<boolean>(false);

// History Management State
export const $historyState = atom<HistoryState>({
  entries: [],
  currentIndex: -1,
  maxEntries: HISTORY_CONFIG.MAX_ENTRIES
});

// Computed history values
export const $canUndo = new ComputedStore([$historyState] as const, (historyState) => {
  return historyState.currentIndex >= 0;
});

export const $canRedo = new ComputedStore([$historyState] as const, (historyState) => {
  return historyState.currentIndex < historyState.entries.length - 1;
});

// State actions with proper typing
export const guestActions = {
  moveGuest: (guestId: number, newTable: number, newSeat: number): void => {
    const guests = $guests.get();
    const updatedGuests = guests.map(guest => 
      guest.id === guestId 
        ? { ...guest, table: newTable, seat: newSeat }
        : guest
    );
    $guests.set(updatedGuests);
  },
  
  unplaceGuest: (guestId: number): void => {
    const guests = $guests.get();
    const updatedGuests = guests.map(guest => 
      guest.id === guestId 
        ? { ...guest, table: null, seat: null }
        : guest
    );
    $guests.set(updatedGuests);
  },
  
  findGuest: (guestId: number): Guest | undefined => {
    return $guests.get().find(g => g.id === guestId);
  },
  
  updateGuest: (guestId: number, updates: Partial<Pick<Guest, 'fullName' | 'table' | 'seat' | 'customField1' | 'customField2'>>): void => {
    const guests = $guests.get();
    const updatedGuests = guests.map(guest => 
      guest.id === guestId 
        ? { ...guest, ...updates }
        : guest
    );
    $guests.set(updatedGuests);
  },
  
  createGuest: (fullName: string, table?: number, seat?: number, customField1?: string, customField2?: string): Guest => {
    const guests = $guests.get();
    const newId = Math.max(0, ...guests.map(g => g.id)) + 1;
    
    const newGuest: Guest = {
      id: newId,
      fullName: fullName.trim(),
      table: table || null,
      seat: seat || null,
      customField1: customField1 || undefined,
      customField2: customField2 || undefined
    };
    
    $guests.set([...guests, newGuest]);
    return newGuest;
  }
};

export const dragActions = {
  startDrag: (
    guestId: number,
    originalPosition: { x: number; y: number },
    originalSeat: string | null
  ): void => {
    $dragState.set({
      isDragging: true,
      draggedGuestId: guestId,
      hoveredSeat: null,
      originalSeat,
      originalPosition
    });
    $uiState.set({ ...$uiState.get(), showDropZones: true });
  },
  
  updateHover: (seatKey: string | null): void => {
    const currentState = $dragState.get();
    $dragState.set({ ...currentState, hoveredSeat: seatKey });
  },
  
  endDrag: (): void => {
    $dragState.set({
      isDragging: false,
      draggedGuestId: null,
      hoveredSeat: null,
      originalSeat: null,
      originalPosition: null
    });
    $uiState.set({ ...$uiState.get(), showDropZones: false });
  }
};

export const versionActions = {
  setCurrentVersion: (versionId: string | null): void => {
    $currentVersionId.set(versionId);
  },
  
  setAvailableVersions: (versions: NamedVersion[]): void => {
    $availableVersions.set(versions);
  },
  
  updateVersion: (updatedVersion: NamedVersion): void => {
    const versions = $availableVersions.get();
    const updatedVersions = versions.map(v => 
      v.id === updatedVersion.id ? updatedVersion : v
    );
    $availableVersions.set(updatedVersions);
  },
  
  addVersion: (newVersion: NamedVersion): void => {
    const versions = $availableVersions.get();
    $availableVersions.set([newVersion, ...versions]);
  },
  
  removeVersion: (versionId: string): void => {
    const versions = $availableVersions.get();
    $availableVersions.set(versions.filter(v => v.id !== versionId));
  },
  
  setAutoSaving: (saving: boolean): void => {
    $isAutoSaving.set(saving);
  }
};

export const historyActions = {
  // Generate unique ID for history entries
  generateId: (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  // Get current version state
  getCurrentState: (): VersionState => {
    return {
      guests: $guests.get(),
      customFields: $customFields.get()
    };
  },

  // Add a new history entry
  addHistoryEntry: (type: HistoryActionType, description: string, beforeState: VersionState, afterState: VersionState, guestId?: number): void => {
    const currentHistory = $historyState.get();
    
    const newEntry: HistoryAction = {
      id: historyActions.generateId(),
      timestamp: new Date(),
      type,
      description,
      beforeState,
      afterState,
      guestId
    };

    // Remove any entries after current index (when undoing then making new changes)
    const newEntries = currentHistory.entries.slice(0, currentHistory.currentIndex + 1);
    newEntries.push(newEntry);

    // Limit history size
    if (newEntries.length > currentHistory.maxEntries) {
      newEntries.shift(); // Remove oldest entry
    } else {
      // Only increment index if we didn't remove an entry
      $historyState.set({
        ...currentHistory,
        entries: newEntries,
        currentIndex: newEntries.length - 1
      });
      return;
    }

    $historyState.set({
      ...currentHistory,
      entries: newEntries,
      currentIndex: newEntries.length - 1
    });
  },

  // Clear history (when switching versions)
  clearHistory: (): void => {
    $historyState.set({
      entries: [],
      currentIndex: -1,
      maxEntries: HISTORY_CONFIG.MAX_ENTRIES
    });
  },

  // Load history from storage
  loadHistory: (entries: HistoryAction[]): void => {
    $historyState.set({
      entries,
      currentIndex: entries.length - 1,
      maxEntries: HISTORY_CONFIG.MAX_ENTRIES
    });
  },

  // Undo: go back one step
  undo: (): VersionState | null => {
    const currentHistory = $historyState.get();
    
    if (currentHistory.currentIndex >= 0) {
      const entryToUndo = currentHistory.entries[currentHistory.currentIndex];
      
      $historyState.set({
        ...currentHistory,
        currentIndex: currentHistory.currentIndex - 1
      });
      
      return entryToUndo.beforeState;
    }
    
    return null;
  },

  // Redo: go forward one step
  redo: (): VersionState | null => {
    const currentHistory = $historyState.get();
    
    if (currentHistory.currentIndex < currentHistory.entries.length - 1) {
      const newIndex = currentHistory.currentIndex + 1;
      const entryToRedo = currentHistory.entries[newIndex];
      
      $historyState.set({
        ...currentHistory,
        currentIndex: newIndex
      });
      
      return entryToRedo.afterState;
    }
    
    return null;
  },

  // Get current history entry for debugging
  getCurrentEntry: (): HistoryAction | null => {
    const currentHistory = $historyState.get();
    if (currentHistory.currentIndex >= 0 && currentHistory.currentIndex < currentHistory.entries.length) {
      return currentHistory.entries[currentHistory.currentIndex];
    }
    return null;
  }
}; 